// ============================================
// COMPANY DETAIL PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Building2, Globe, MapPin, Ship, Star, Mail, Phone, Calendar, ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { PageHeader, VerificationBadge } from '@/components';
import { adminApi } from '@/services/api';
import { formatDate } from '@/utils';

export const Route = createFileRoute('/_authenticated/companies/$id/')({
  component: CompanyDetailPage,
});

function CompanyDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const { data: companyData, isLoading } = useQuery({
    queryKey: ['admin', 'companies', id],
    queryFn: () => adminApi.getCompany(id),
  });

  // Handle both direct and wrapped response formats
  const company = (companyData as any)?.data || companyData;

  // Doğrulama toggle mutation
  const verifyMutation = useMutation({
    mutationFn: (isVerified: boolean) => adminApi.updateCompany(id, { isVerified }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maritime-600"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Şirket bulunamadı</p>
        <Link to="/companies" className="text-maritime-600 hover:underline mt-2 inline-block">
          Şirketlere Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Şirket Detayı"
        description={company.name}
        actions={
          <div className="flex gap-2">
            <Link
              to="/companies"
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-maritime hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </Link>
            <Link
              to={`/companies/${id}/edit` as any}
              className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700"
            >
              <Edit className="h-4 w-4" />
              Düzenle
            </Link>
          </div>
        }
      />

      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-maritime bg-maritime-100 text-maritime-700 font-bold text-2xl">
            {company.logoUrl ? (
              <img src={company.logoUrl} alt={company.name} className="w-full h-full object-cover rounded-maritime" />
            ) : (
              company.name.charAt(0)
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">{company.name}</h2>
              <VerificationBadge isVerified={company.isVerified} />
            </div>
            {company.description && (
              <p className="text-gray-600 mt-1">{company.description}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            {company.website && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-maritime-100 rounded-full">
                  <Globe className="h-5 w-5 text-maritime-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Website</p>
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="font-medium text-maritime-600 hover:underline">
                    {company.website}
                  </a>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <MapPin className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Konum</p>
                <p className="font-medium">{company.city ? `${company.city}, ` : ''}{company.country}</p>
              </div>
            </div>

            {company.contactEmail && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-maritime-100 rounded-full">
                  <Mail className="h-5 w-5 text-maritime-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">İletişim E-posta</p>
                  <p className="font-medium">{company.contactEmail}</p>
                </div>
              </div>
            )}

            {company.contactPhone && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-maritime-100 rounded-full">
                  <Phone className="h-5 w-5 text-maritime-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">İletişim Telefon</p>
                  <p className="font-medium">{company.contactPhone}</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Ship className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Filo Büyüklüğü</p>
                <p className="font-medium">{company.fleetSize || 0} gemi</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Building2 className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam İlan</p>
                <p className="font-medium">{company.totalJobsPosted || 0}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Star className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Değerlendirme</p>
                <p className="font-medium">{company.averageRating?.toFixed(1) || '0.0'} ({company.reviewCount || 0} değerlendirme)</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-maritime-100 rounded-full">
                <Calendar className="h-5 w-5 text-maritime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                <p className="font-medium">{formatDate(company.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Aksiyonları */}
      <div className="bg-white rounded-maritime shadow-maritime p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Admin Aksiyonları</h3>
        <div className="flex items-center gap-4">
          <button
            onClick={() => verifyMutation.mutate(!company.isVerified)}
            disabled={verifyMutation.isPending}
            className={`flex items-center gap-2 px-4 py-2 rounded-maritime text-sm font-medium transition-colors disabled:opacity-50 ${
              company.isVerified
                ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
            }`}
          >
            {verifyMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : company.isVerified ? (
              <ShieldOff className="h-4 w-4" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            {company.isVerified ? 'Doğrulamayı Kaldır' : 'Doğrula'}
          </button>
        </div>
      </div>
    </div>
  );
}
